import React from 'react'
import type { ICellRendererParams } from 'ag-grid-community'
import { Vertical, Texto, Horizontal } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import type { MatchBol, MatchInvoice } from '@api/useSupplierReconciliation/types/match'

interface ProductsCellRendererProps extends ICellRendererParams<MatchBol | MatchInvoice> {
  value: string[]
}

interface ProductRatio {
  name: string
  amount?: number
  volume: number
}

function ProductPill({ ratio }: { ratio: ProductRatio }) {
  if (!ratio) return null

  const formattedVolume = ratio.volume > 0 ? `${Math.round(ratio.volume).toLocaleString()} L` : ratio.name

  return (
    <Tooltip placement='top' title={formattedVolume}>
      <div>
        <Horizontal alignItems='stretch' className='mr-2 mb-1' style={{ overflow: 'visible' }}>
          {ratio.amount !== undefined && (
            <Vertical
              verticalCenter
              borderRadius='6px 0 0 6px'
              className='px-1'
              style={{
                minWidth: 16,
                background: 'var(--gray-700)',
                height: '100%',
                border: 'solid 1px var(--gray-700)',
              }}
            >
              <Texto style={{ color: 'var(--gray-100)' }} weight='bold' align='center' category='label'>
                {ratio.amount}
              </Texto>
            </Vertical>
          )}

          <Vertical
            verticalCenter
            className='px-1'
            style={{
              fontWeight: 600,
              textTransform: 'uppercase',
              background: 'var(--bg-2)',
              borderRadius: ratio.amount !== undefined ? '0 6px 6px 0' : '6px',
              height: '100%',
              border: 'solid 1px var(--gray-300)',
              whiteSpace: 'nowrap',
            }}
          >
            <Texto category='label'>{ratio.name}</Texto>
          </Vertical>
        </Horizontal>
      </div>
    </Tooltip>
  )
}

function getProductRatiosFromBol(data: MatchBol): ProductRatio[] {
  if (!data.products || data.products.length === 0) {
    return []
  }

  return data.products.map((product: { product_name?: string; net_volume?: number; gross_volume?: number }) => ({
    name: product.product_name || 'Unknown Product',
    amount: product.gross_volume ? Math.round(product.gross_volume / 1000) : undefined,
    volume: product.gross_volume || product.net_volume || 0,
  }))
}

function getProductRatiosFromInvoice(data: MatchInvoice): ProductRatio[] {
  const details = (data as any)?.details || []
  if (!details || details.length === 0) {
    return []
  }

  const productMap = new Map<string, { totalVolume: number; count: number }>()

  details.forEach((detail: any) => {
    if (detail.product && detail.type !== 'tax') {
      const productName = detail.product.internal_name || detail.product.source_name
      const volume = detail.gross_volume || detail.net_volume || 0

      if (productName) {
        if (productMap.has(productName)) {
          const existing = productMap.get(productName)!
          existing.totalVolume += volume
          existing.count += 1
        } else {
          productMap.set(productName, { totalVolume: volume, count: 1 })
        }
      }
    }
  })

  return Array.from(productMap.entries()).map(([name, data]) => ({
    name,
    amount: data.totalVolume > 0 ? Math.round(data.totalVolume / 1000) : undefined,
    volume: data.totalVolume,
  }))
}

function isBolData(data: MatchBol | MatchInvoice): data is MatchBol {
  return 'products' in data && Array.isArray(data.products)
}

export function ProductsCellRenderer({ data }: ProductsCellRendererProps) {
  if (!data) return null

  let ratios: ProductRatio[] = []

  if (isBolData(data)) {
    ratios = getProductRatiosFromBol(data)
  } else {
    ratios = getProductRatiosFromInvoice(data)
  }

  if (ratios.length === 0) {
    return (
      <Texto category='p2' appearance='hint'>
        N/A
      </Texto>
    )
  }

  return (
    <div className='flex' style={{ flexWrap: 'wrap' }}>
      {ratios.map((ratio, index) => (
        <ProductPill key={index} ratio={ratio} />
      ))}
    </div>
  )
}
